









































































































































































































































































































































































































































































































































// 3rd party libraries
import { PropType } from 'vue';
import { mapActions } from 'vuex';
import moment from 'moment';
import { get } from 'vuex-pathify';

// utilities
import mixins from '@/utils/mixins';

// mixins
import auth from '@/mixins/auth';
import componentState from '@/mixins/state';
import snackbar from '@/mixins/snackbar';

// models
import { IQuote, QuoteType, IQuoteProductCategory } from '@/models/quote';
import { Customer } from '@/models/customer';
import { CurrencyRate, ICurrency } from '@/models/currency';
import { Module, ModulePaymentTypeEnum } from '@/models/configurator/module';

// Components
import QuoteProductSubscriptionItemBody from './QuoteProductSubscriptionItemBody.vue';
import QuoteProductItemBody from './QuoteProductItemBody.vue';
import QuoteServiceItemsSection from './QuoteServiceItemsSection.vue';
import QuoteExtraItemsSection from './QuoteExtraItemsSection.vue';
import QuotePaymentTermItemsSection from './QuotePaymentTermItemsSection.vue';
import QuoteFreightItemsSection from './QuoteFreightItemsSection.vue';

export default mixins(auth, componentState, snackbar).extend({
  name: 'QuoteProposalSummaryDetailCard',

  components: {
    QuoteProductSubscriptionItemBody,

    QuoteProductItemBody,
    QuoteServiceItemsSection,
    QuoteExtraItemsSection,
    QuoteFreightItemsSection,
    QuotePaymentTermItemsSection
  },

  props: {
    proposal: { type: Object as PropType<IQuote> }
  },

  data: () => ({
    equipmentQuoteCategories: [] as IQuoteProductCategory[],
    subscriptionQuoteCategories: [] as IQuoteProductCategory[],

    loading: false
  }),

  computed: {
    quote: {
      get(): IQuote {
        return this.proposal;
      },
      set(value: IQuote) {
        this.$emit('update:proposal', value);
      }
    },

    getCustomerById: get<(id: string) => Customer>('customer/getCustomerById'),

    getCurrencyById: get<(id: number) => CurrencyRate>(
      'currency/getCurrencyById'
    ),

    currencyCode(): string {
      return (this.getCurrencyById(this.quote.currency!).currency as ICurrency)
        .code;
    },

    getModuleById: get<(id: number) => Module>(
      'configuratorModule/getModuleById'
    ),

    airwayIcon: get('app/airwayIcon'),

    allowEwgDiscount(): boolean {
      if (this.quote.customer?.identifier) {
        return (
          this.getCustomerById(this.quote.customer.identifier)?.type === 'EWG'
        );
      }

      return false;
    },

    isQuoteVentilation(): boolean {
      return (
        this.quote.type == QuoteType.RTU_VENTILATION ||
        this.quote.type == QuoteType.AIRWAY_VENTILATION
      );
    },

    equipmentDiscountsTotal(): number {
      // TODO: plus item discount as well

      const reducer = (accumulator: number, currentQuote: number) =>
        accumulator + currentQuote;
      return (
        this.quote.categories
          .map(category => {
            const module = this.getModuleById(category.module);
            let discount = 0;
            if (!module.is_subscription) {
              discount = category.discount!;
            }
            return discount;
          })
          .reduce(reducer, 0) || 0
      );
    },

    equipmentSubTotal(): number {
      return (
        this.quote.categories.reduce((accumulator, category) => {
          let total = accumulator;
          const module = this.getModuleById(category.module);
          if (!module.is_subscription) {
            total += category.price ?? 0;
          }
          return total;
        }, 0) - this.equipmentDiscountsTotal
      );
    },

    oneTimeSubscriptionModuleQuoteCategories(): IQuoteProductCategory[] {
      return this.filterSubscriptionQuoteCategoriesByModulePaymentType(
        ModulePaymentTypeEnum.OneTime
      );
    },

    recurringSubscriptionModuleQuoteCategories(): IQuoteProductCategory[] {
      return this.filterSubscriptionQuoteCategoriesByModulePaymentType(
        ModulePaymentTypeEnum.Recurring
      );
    },

    subscriptionDiscountsTotal(): number {
      // TODO: plus item discount as well

      const reducer = (accumulator: number, currentQuote: number) =>
        accumulator + currentQuote;
      return (
        this.quote.categories
          .map(category => {
            const module = this.getModuleById(category.module);
            let discount = 0;
            if (module.is_subscription) {
              discount = category.discount!;
            }
            return discount;
          })
          .reduce(reducer, 0) || 0
      );
    },

    subscriptionSubTotal(): number {
      return (
        this.quote.categories.reduce((accumulator, category) => {
          let total = accumulator;
          const module = this.getModuleById(category.module);
          if (module.is_subscription) {
            total += category.price ?? 0;
          }
          return total;
        }, 0) - this.subscriptionDiscountsTotal
      );
    },

    oneTimeSubscriptionFeeSubTotal(): number {
      return this.oneTimeSubscriptionModuleQuoteCategories.reduce(
        (accumulator, category) => {
          let total = accumulator;

          const netPrice = category.price! - category.discount!;

          total += netPrice!;

          return total;
        },
        0
      );
    },

    oneTimeSubscriptionFirstYearDiscountsTotal(): number {
      return this.oneTimeSubscriptionModuleQuoteCategories.reduce(
        (accumulator, category) => {
          let discountTotal = accumulator;

          const netPrice = category.price! - category.discount!;

          const firstYearSubscriptionDiscount =
            (category.first_year_subscription_discount_percentage! * netPrice) /
            100;

          discountTotal += firstYearSubscriptionDiscount;

          return discountTotal;
        },
        0
      );
    },

    recurringSubscriptionFeeTotal(): number {
      return this.recurringSubscriptionModuleQuoteCategories.reduce(
        (accumulator, category) => {
          let total = accumulator;

          const netPrice = category.price! - category.discount!;

          total += netPrice!;

          return total;
        },
        0
      );
    },

    oneTimeSubscriptionFeeNetTotal(): number {
      return (
        this.oneTimeSubscriptionFeeSubTotal -
        this.oneTimeSubscriptionFirstYearDiscountsTotal
      );
    },

    equipmentNetTotal(): number {
      return (
        this.equipmentSubTotal -
        Number(this.quote.special_discount!) -
        Number(this.quote.ewg_discount!)
      );
    },

    subscriptionNetTotal(): number {
      return (
        this.subscriptionSubTotal -
        Number(this.quote.subscription_first_year_discount!)
      );
    },

    amountTotal(): number {
      return [
        this.quote.service_items,
        this.quote.freight_items,
        this.quote.extra_items
      ]
        .flat()
        .map(item => Number(item.price! * this.quote.exchange_rate!))
        .reduce(
          (accumulator, cummalative) => accumulator + cummalative,
          this.equipmentNetTotal +
            this.subscriptionNetTotal +
            this.quote.net_surcharge
        );
    }
  },

  watch: {
    quote: {
      handler(value) {
        this.setQuoteCategoryGroups(value);
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    ...mapActions({
      fetchModule: 'configuratorModule/fetchModule'
    }),

    async setQuoteCategoryGroups(quote: IQuote) {
      this.equipmentQuoteCategories = [];
      this.subscriptionQuoteCategories = [];
      for (let i = 0; i < quote.categories.length; i++) {
        const item = quote.categories[i];
        const module = await this.tryGetOrFetchModule(item.module);
        if (module.is_subscription) {
          this.subscriptionQuoteCategories.push(item);
        } else {
          this.equipmentQuoteCategories.push(item);
        }
      }
    },

    async tryGetOrFetchModule(id: number) {
      let module = this.getModuleById(id);
      if (!module) {
        module = await this.fetchModule(id);
      }
      return module;
    },

    momentFormat(date: Date | string | number, days = 0) {
      return moment(date ?? new Date())
        .add(days, 'd')
        .format('LL')
        .toString();
    },

    filterSubscriptionQuoteCategoriesByModulePaymentType(
      paymentType: ModulePaymentTypeEnum
    ): IQuoteProductCategory[] {
      return this.subscriptionQuoteCategories.filter(category => {
        const module = this.getModuleById(category.module);
        return module && module.payment_type == paymentType;
      });
    }
  }
});
